import { useAuth0 } from '@auth0/auth0-react';
import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useDebounceValue } from '@/hooks';

import { ReeferKpiByModel } from './ReeferKpiByModel';
import { ReeferKpiByModelInput } from './ReeferKpiByModelInput';
import { ReeferKpiByModelResponse } from './ReeferKpiByModelResponse';

const {
  VITE_GREENSEE_API_URL,
  VITE_AUTH0_AUDIENCE,
  VITE_APP_ENERGY_USAGE_QUERIES_DEBOUNCE_MS,
} = import.meta.env;

const EnergyUsageQueriesDebounceMs = parseInt(
  VITE_APP_ENERGY_USAGE_QUERIES_DEBOUNCE_MS,
);

export function useReeferKpiByModel(
  params: ReeferKpiByModelInput,
): [UseQueryResult<ReeferKpiByModel, Error>, () => void] {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const body = JSON.stringify(params);

  const debouncedKey = useDebounceValue(body);
  const queryKey = ['greensee-api-post-reefer-kpi', debouncedKey];

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: VITE_AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });

      const response = await fetch(`${VITE_GREENSEE_API_URL}/reefer-kpi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.access_token}`,
        },
        body,
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(
          `Error while fetching Reefer KPI: Invalid Content-Type response ${response.status}`,
        );
      }

      const dataset: ReeferKpiByModel = await response.json();

      if (!response.ok) {
        throw new Error(`Error while fetching Reefer KPI: ${response.status}`);
      }

      return dataset;
    },
  });

  return [result, () => queryClient.invalidateQueries({ queryKey })];
}
